import { useRef } from 'react';
import { generatePath, NavLink, Outlet } from 'react-router-dom';
import {
  IconExpand,
  IconLoader,
  Table,
  PermissionEnum,
  IconSelected,
  IconArrowRight,
  PermissionDenied,
  InputText,
  IconSearch,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setHintVisible } from '../store/slices/list';
import {
  corporateCustomersListColumns,
  corporateCustomersListColumnsLabels,
} from './variables/list';
import useList from './hooks/use-list';

import ROUTES from '../../../routes';

import commonStyles from '../../common/common.module.css';

const CorporateList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { incorporationDates, dictionariesIsLoading, applicationSchemes } =
    useAppSelector((state) => state.customersDictionaries);
  const { baseInformation, generalInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInfo } = useAppSelector(
    (state) => state.corporateCustomers.info.overview,
  );
  const {
    corporateCustomers,
    pagination,
    hintVisible,
    createdApplicationId,
    errorsList,
  } = useAppSelector((state) => state.corporateCustomers.list);
  const { countriesList: countries, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const dispatch = useAppDispatch();

  const {
    isLoading,
    countriesList,
    formattedData,
    onSort,
    filters,
    sort,
    companyNameSearch,
    filtersComponent,
    handleSubmit,
    isFetching,
    onTableFilterChange,
    onTableSetPage,
    search,
    errorList,
    onCompanySearch,
  } = useList({
    pagination,
    countries,
    corporateCustomers,
    incorporationDates,
    scrollRef,
    generalInformation,
    baseInformation,
    baseInfo,
    applicationSchemes,
    activePermissions,
  });

  let content;

  if (isLoading || dictionariesIsLoading) {
    content = (
      <div className={commonStyles.customersLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    countriesList &&
    activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_LIST)
  ) {
    content = (
      <Table
        columns={corporateCustomersListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={corporateCustomersListColumnsLabels}
        onFilterChange={(values) => onTableFilterChange(values)}
        hiddenFiltersTags={
          filters?.status?.value.length === 2 ? ['status'] : []
        }
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        tableFormClassName={commonStyles.customersListForm}
        take="20"
        setPage={(data) => onTableSetPage(data)}
        pagination={pagination}
        error={errorsList || errorList}
        withSettings
        clickable
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={commonStyles.customersBottom} ref={scrollRef}>
      <div className={commonStyles.customersTopContent}>
        <div className={commonStyles.customerTopContentTitle}>
          Corporate Customers
        </div>
        <NavLink
          to={`${ROUTES.CUSTOMERS_CORPORATE_CREATE}${search || ''}`}
          className={commonStyles.customersCreateLink}
        >
          <div className={commonStyles.customersCreateLinkContent}>
            <IconExpand className={commonStyles.customersCreateLinkIcon} />
            <span>Create Corporate Customers</span>
          </div>
        </NavLink>
        <InputText
          name="nameSearch"
          labelClassName={commonStyles.nameSearch}
          value={companyNameSearch}
          placeholder="Enter company name"
          prefix={<IconSearch />}
          prefixClassName={commonStyles.nameSearchIcon}
          onChange={(e) => onCompanySearch(e.target.value)}
        />
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={commonStyles.applicationCreatedSuccessful}>
          <div className={commonStyles.applicationCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={commonStyles.applicationCreatedSuccessfulRight}>
            <span>
              {createdApplicationId && (
                <NavLink
                  to={generatePath(
                    ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
                    { applicationId: createdApplicationId },
                  )}
                >
                  <IconArrowRight />
                  Open application
                </NavLink>
              )}
            </span>
            <button
              type="button"
              className={commonStyles.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateList;
